import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Issue } from './types';

interface IssuesKanbanViewProps {
  issues: Issue[];
  onEditIssue: (issue: Issue) => void;
  onReorder: (nextIssues: Issue[], items: Array<{ id: string; status: string; boardOrder: number }>) => void;
  canDrag?: boolean;
  canEdit?: boolean;
}

const columns = [
  { id: 'new', title: 'Nya', color: '#e3f2fd', borderColor: '#1976d2' },
  { id: 'in_progress', title: 'Pågår', color: '#fff3e0', borderColor: '#f57c00' },
  { id: 'ready_for_test', title: 'Redo för test', color: '#e8f5e9', borderColor: '#388e3c' },
  { id: 'done', title: 'Klart', color: '#f5f5f5', borderColor: '#9e9e9e' },
] as const;

const typeLabel: Record<string, string> = {
  bug: 'Bug',
  fix: 'Fix',
  feature: 'Feature',
  improvement: 'Improvement',
};

const priorityColor = (priority: number) => {
  if (priority === 1) return 'error';
  if (priority === 2) return 'warning';
  return 'default';
};

const IssuesKanbanView: React.FC<IssuesKanbanViewProps> = ({ issues, onEditIssue, onReorder, canDrag = true, canEdit = true }) => {
  const grouped = useMemo(() => {
    const initial: Record<string, Issue[]> = {
      new: [],
      in_progress: [],
      ready_for_test: [],
      done: [],
    };
    issues.forEach((issue) => {
      (initial[issue.status] || initial.new).push(issue);
    });
    Object.keys(initial).forEach((status) => {
      initial[status] = initial[status].sort((a, b) => {
        const aOrder = Number.isFinite(a.boardOrder) ? a.boardOrder : 0;
        const bOrder = Number.isFinite(b.boardOrder) ? b.boardOrder : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      });
    });
    return initial;
  }, [issues]);

  const handleDragEnd = (result: DropResult) => {
    if (!canDrag) return;
    if (!result.destination) return;
    const sourceStatus = result.source.droppableId;
    const destStatus = result.destination.droppableId;
    if (!grouped[sourceStatus] || !grouped[destStatus]) return;

    const sourceList = Array.from(grouped[sourceStatus]);
    const destList = sourceStatus === destStatus ? sourceList : Array.from(grouped[destStatus]);

    const [moved] = sourceList.splice(result.source.index, 1);
    destList.splice(result.destination.index, 0, moved);

    const nextByStatus: Record<string, Issue[]> = {
      ...grouped,
      [sourceStatus]: sourceList,
      [destStatus]: destList,
    };

    const updateMap = new Map<string, { status: string; boardOrder: number }>();
    Object.entries(nextByStatus).forEach(([status, list]) => {
      list.forEach((issue, index) => {
        updateMap.set(issue._id, { status, boardOrder: (index + 1) * 10 });
      });
    });

    const updates: Array<{ id: string; status: string; boardOrder: number }> = [];
    const nextIssues = issues.map((issue) => {
      const update = updateMap.get(issue._id);
      if (!update) return issue;
      updates.push({ id: issue._id, status: update.status, boardOrder: update.boardOrder });
      return { ...issue, status: update.status as any, boardOrder: update.boardOrder };
    });

    onReorder(nextIssues, updates);
  };

  if (!issues.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Inga ärenden ännu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Klicka på "Nytt ärende" ovan för att skapa ditt första ärende.
        </Typography>
      </Paper>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
        {columns.map((column) => (
          <Droppable key={column.id} droppableId={column.id}>
            {(provided, snapshot) => (
              <Paper
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  minWidth: 280,
                  width: 320,
                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'grey.50',
                  borderTop: `3px solid ${column.borderColor}`,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ 
                  backgroundColor: column.color, 
                  px: 2, 
                  py: 1.5,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {column.title}
                    <Chip 
                      size="small" 
                      label={grouped[column.id]?.length || 0} 
                      sx={{ 
                        height: 22, 
                        minWidth: 28,
                        fontWeight: 600,
                        backgroundColor: 'white',
                      }} 
                    />
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, minHeight: 100, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(grouped[column.id] || []).map((issue, index) => (
                    <Draggable key={issue._id} draggableId={issue._id} index={index} isDragDisabled={!canDrag}>
                      {(dragProvided, dragSnapshot) => (
                        <Paper
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          onClick={() => { if (canEdit) onEditIssue(issue); }}
                          elevation={dragSnapshot.isDragging ? 4 : 1}
                          sx={{
                            p: 1.5,
                            cursor: canEdit ? 'pointer' : 'default',
                            borderLeft: `4px solid`,
                            borderLeftColor: issue.priority === 1 ? 'error.main' : issue.priority === 2 ? 'warning.main' : 'grey.300',
                            backgroundColor: dragSnapshot.isDragging ? 'grey.100' : 'background.paper',
                            transition: 'box-shadow 0.2s, background-color 0.2s',
                            '&:hover': canEdit ? {
                              backgroundColor: 'grey.50',
                            } : {},
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            {issue.title}
                          </Typography>
                          {issue.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {issue.description.length > 60 ? issue.description.substring(0, 60) + '...' : issue.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={`P${issue.priority}`} color={priorityColor(issue.priority)} sx={{ height: 20, fontSize: '0.7rem' }} />
                            <Chip size="small" label={typeLabel[issue.type] || issue.type} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            {issue.attachments?.length ? (
                              <Chip size="small" label={`📎 ${issue.attachments.length}`} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            ) : null}
                          </Box>
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              </Paper>
            )}
          </Droppable>
        ))}
      </Box>
    </DragDropContext>
  );
};

export default IssuesKanbanView;
